// hunger-thirst-info.ts — client-side render of the creature Hunger / Thirst side panel,
// reading from window.FateTide.casterCache (no DB ping on a cache hit).
//
// Replaces the AJAX PHP panels:
//   options/tools/encounter_creatures/encounter_creature_info/creature_hunger_info_inc.php
//   options/tools/encounter_creatures/encounter_creature_info/creature_thirst_info_inc.php
//
// The Adjust button goes to the resource truth seam (websocket/resource-truth.ts ftResourceAdjust — the browser decides,
// resource_truth_commit_inc.php lazily saves; N6 2026-10-01: the adjusts/*.php endpoints are gone). It never force-refreshes
// the sheet (refreshCasterSheet) nor re-AJAXes this panel: the saved row arrives as a caster_update, which is the single source
// of truth. That push
//   (a) refreshes window.FateTide.casterCache,
//   (b) repaints the Defenses-pane meters (ftRerenderPcSheet), and
//   (c) re-renders THIS open panel via setActiveInfoPanel → ftRerenderOpenInfoPanel.
// So the screen converges from the encounterArr/caster blob, not a hard PHP re-query.

import { hasPlayerPermission } from '../shared/array-types';
import { setActiveInfoPanel } from './active-panel';
import type { CachedCaster } from './types';

type Meter = 'hunger' | 'thirst';

function unwrap(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : v;
}
function num(v: unknown): number {
    const n = Number(unwrap(v));
    return Number.isFinite(n) ? n : 0;
}

/** layer_value(caster, '<which>_meter_max') — bridged to computed.creature.meters, else the layer.
 *  Null when the creature has no meter max → it does not use hunger/thirst (server only computes it
 *  for sized player_characters — creature_finalize_helpers.php). */
function meterMax(caster: CachedCaster, which: Meter): number | null {
    const bridged = caster.computed?.creature?.meters?.[`${which}_max`];
    if (bridged !== undefined && bridged !== null) return num(bridged);
    const layer = ftLayerValue(caster, `${which}_meter_max`);
    return layer === undefined || layer === null ? null : num(layer);
}

function casterName(caster: CachedCaster): string {
    const ec = caster.encounter_creatures && !Array.isArray(caster.encounter_creatures)
        ? caster.encounter_creatures
        : undefined;
    const raw = unwrap(caster.items?.item_name)
        ?? unwrap(caster.player_characters?.player_name)
        ?? unwrap(ec?.encounter_creature_name);
    return String(raw ?? '').trim();
}

function renderMeterPanel(caster: CachedCaster, casterId: string, which: Meter): string {
    const label = which === 'hunger' ? 'Hunger' : 'Thirst';
    const max = meterMax(caster, which);
    if (max === null) {
        return `<h5 class="p-3 text-center">This creature does not use hunger or thirst mechanics.</h5>`;
    }

    const ecArr = (caster.encounter_creatures ?? {}) as Record<string, unknown>;
    const encounterCreatureId = String(unwrap(ecArr.encounter_creature_id) ?? '').trim();
    const rawCur = ecArr[`${which}_meter_current`];
    // Out of encounter (no live meter row) show FULL, mirroring the Defenses-pane meters
    // (buildHungerThirstHtml) — a real persisted out-of-encounter current is a separate data change.
    const cur = (rawCur === undefined || rawCur === null) ? max : num(rawCur);

    const name = casterName(caster);

    let html = `
    <table class="table border mb-3">
        <thead><tr><th scope="col" colspan="2" class="table-dark align-middle text-center">
            <h5 class="mb-0">${iconMap(which)} ${name ? escapeHtml(name) + ' ' : ''}${label}:</h5>
        </th></tr></thead>
        <tbody>
            <tr><th scope="row" class="align-middle">${label} Meter Current:</th>
                <td class="text-center"><h3 class="mb-0"><span class="badge bg-info">${cur}</span></h3></td></tr>
            <tr><th scope="row" class="align-middle">${label} Meter Max:</th>
                <td class="text-center"><h3 class="mb-0"><span class="badge bg-warning">${max}</span></h3></td></tr>
        </tbody>
    </table>`;

    // Adjust: owner/permission AND in-encounter only. The write endpoint mutates the encounter
    // creature's meter; out of encounter there is no row to adjust, so hide it there.
    if (hasPlayerPermission(caster) && encounterCreatureId !== '') {
        const inputId    = `input_number_adjust_${which}`;
        const baselineId = `ec_${which}_baseline`;
        const rangeId    = `ec_${which}_range`;
        const labelId    = `ec_${which}_range_label`;
        const sliderVal  = Math.min(Math.max(0, cur), max);

        // Slider ⇄ delta two-way sync (vanilla JS, mirrors the mana / vitality panels). The slider sets
        // an ABSOLUTE target [0..max]; the value POSTED is the DELTA (target − baseline current), which
        // is exactly what the resource seam expects.
        const sliderOnInput = `(function(){var b=parseInt(document.getElementById('${baselineId}').value,10)||0;var t=parseInt(document.getElementById('${rangeId}').value,10)||0;var d=t-b;var h=document.getElementById('${inputId}');if(h){h.value=d;}var lbl=document.getElementById('${labelId}');if(lbl){lbl.textContent=t;}})()`;
        const deltaOnInput  = `(function(){var b=parseInt(document.getElementById('${baselineId}').value,10)||0;var d=parseInt(document.getElementById('${inputId}').value,10);if(isNaN(d))d=0;var r=document.getElementById('${rangeId}');if(!r)return;var mn=parseInt(r.min,10);if(isNaN(mn))mn=0;var mx=parseInt(r.max,10);if(isNaN(mx))mx=0;var t=Math.min(mx,Math.max(mn,b+d));r.value=t;var lbl=document.getElementById('${labelId}');if(lbl){lbl.textContent=t;}})()`;

        // No refreshDiv override / no refreshCasterSheet — the caster_update WS push is the source of
        // truth: it repaints the Defenses-pane meters and re-renders this panel (see file header).
        const handler =
            '(async()=>{try{'
            // The resource truth seam (websocket/resource-truth.ts ftResourceAdjust) — never a legacy post (N6 2026-10-01).
            + `await window.ftResourceAdjust('${which}','${encounterCreatureId}','${inputId}');`
            + `}catch(e){console.error('Error updating ${which}:',e);}})()`;

        html += `
        <div class="p-1 mt-2 mb-2 border rounded">
            <input type="hidden" id="${baselineId}" value="${cur}">
            <div class="form-group mb-3">
                <h5 class="text-center mb-2">${iconMap(which)} Set current ${label.toLowerCase()}</h5>
                <div class="d-flex align-items-center px-1">
                    <span class="small text-muted mr-2">0</span>
                    <input type="range" id="${rangeId}" class="form-range flex-grow-1 mx-1" min="0" max="${max}" step="1" value="${sliderVal}" aria-label="Set current ${label.toLowerCase()}" oninput="${escapeHtml(sliderOnInput)}">
                    <span class="small text-muted ml-2">${max}</span>
                </div>
                <div class="text-center small text-muted" id="${labelId}">${sliderVal}</div>
                <p class="small text-muted text-center mb-0">Drag to your target current ${label.toLowerCase()}, then tap Adjust below.</p>
            </div>
            <div class="form-group mb-3">
                <h5 class="text-center">Adjust (delta):</h5>
                <input id="${inputId}" class="form-control text-center" type="number" value="0" oninput="${escapeHtml(deltaOnInput)}">
            </div>
            <div class="text-center">
                <button class="btn btn-primary" onclick="${escapeHtml(handler)}">${iconMap(which)} Adjust ${label}</button>
            </div>
        </div>`;
    }

    return html;
}

async function renderMeterInfoPanel(casterId: string, targetId: string, which: Meter): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) return;

    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }

    const render = (c: CachedCaster) => renderMeterPanel(c, casterId, which);
    setActiveInfoPanel(casterId, targetId, render);
    target.innerHTML = render(caster);
}

export function renderHungerInfoPanel(casterId: string, targetId = 'div_refresh_side_info'): Promise<void> {
    return renderMeterInfoPanel(casterId, targetId, 'hunger');
}
export function renderThirstInfoPanel(casterId: string, targetId = 'div_refresh_side_info'): Promise<void> {
    return renderMeterInfoPanel(casterId, targetId, 'thirst');
}
